import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nabvar from "../../common/Nabvar";
import Sidebar from "../../common/Sidebar";
import axios from "../../config/authaxios";
import Body from "../../ui/Body";
import DenominationTable from "./DenominationTable";
import {
  buildDenominationRows,
  buildDiscountPayload,
  fetchEligibleVouchers,
  fetchSkuDenominations,
  formatVoucherOptionLabel,
  syncDenominationRowForType,
  validateDiscountForm,
} from "./discountUtils";

export default function BulkDiscount() {
  const navigate = useNavigate();

  const [publishers, setPublishers] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [publisherId, setPublisherId] = useState("");
  const [templateSku, setTemplateSku] = useState("");
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [allowedDenominations, setAllowedDenominations] = useState([]);
  const [loadingDenoms, setLoadingDenoms] = useState(false);
  const [form, setForm] = useState({
    discount_enabled: false,
    discount_type: "upto",
    denominations: [],
    max_discount_cap: 0,
    cashback_percent: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const templateVoucher = vouchers.find((v) => v.sku === templateSku);

  useEffect(() => {
    axios.get("/admin/publishers/name-id").then((res) => {
      if (res.data.status) setPublishers(res.data.response || []);
    });
    fetchEligibleVouchers(axios).then(setVouchers);
  }, []);

  const loadTemplateDenominations = (sku) => {
    if (!sku) {
      setAllowedDenominations([]);
      setForm((prev) => ({ ...prev, denominations: [] }));
      return;
    }

    setLoadingDenoms(true);
    fetchSkuDenominations(axios, sku)
      .then((data) => {
        setAllowedDenominations(data.denominations || []);
        setForm((prev) => ({
          ...prev,
          denominations: buildDenominationRows(data.denominations),
        }));
      })
      .catch((err) => {
        setAllowedDenominations([]);
        setForm((prev) => ({ ...prev, denominations: [] }));
        setSnackbar({
          open: true,
          message: err.message || "Failed to load template SKU denominations",
          severity: "error",
        });
      })
      .finally(() => setLoadingDenoms(false));
  };

  const updateField = (field, value) => {
    setForm((prev) => {
      let next = { ...prev, [field]: value };
      if (field === "discount_type" && value === "fixed") {
        next.denominations = next.denominations.map((row) =>
          syncDenominationRowForType(row, "fixed")
        );
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!publisherId) {
      setErrors({ publisher_id: "Publisher is required" });
      return;
    }
    if (!templateSku) {
      setErrors({ template_sku: "Template SKU is required to define denominations" });
      return;
    }
    if (selectedSkus.length === 0) {
      setErrors({ skus: "Select at least one SKU" });
      return;
    }

    const validationErrors = validateDiscountForm(
      form,
      allowedDenominations,
      templateVoucher
    );
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setResult(null);

    const base = buildDiscountPayload(form, false);
    delete base.sku;

    axios
      .post("/admin/discount/bulk", {
        publisher_id: publisherId,
        skus: selectedSkus,
        ...base,
      })
      .then((res) => {
        if (res.data.status_code === 401) localStorage.clear();
        if (res.data.status) {
          const data = res.data.response;
          setResult(data);
          const createdCount = data.created?.length || 0;
          const failedCount = data.failed?.length || 0;
          setSnackbar({
            open: true,
            message: `Created ${createdCount}, failed ${failedCount}`,
            severity: failedCount > 0 ? "warning" : "success",
          });
        } else {
          setSnackbar({ open: true, message: res.data.message || "Bulk create failed", severity: "error" });
        }
      })
      .catch((err) => {
        setSnackbar({
          open: true,
          message: err.response?.data?.message || "Something went wrong",
          severity: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Sidebar />
      <Nabvar />
      <Body>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/discounts")}>
            Back
          </Button>
          <Typography variant="h5">Bulk Assign Discounts</Typography>
        </Stack>

        <Card sx={{ p: 3, maxWidth: 760 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <FormControl fullWidth error={Boolean(errors.publisher_id)}>
                <InputLabel>Publisher *</InputLabel>
                <Select
                  value={publisherId}
                  label="Publisher *"
                  onChange={(e) => setPublisherId(e.target.value)}
                >
                  {publishers.map((p) => (
                    <MenuItem key={p._id} value={p._id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.publisher_id && <FormHelperText>{errors.publisher_id}</FormHelperText>}
              </FormControl>

              <Autocomplete
                options={vouchers}
                getOptionLabel={formatVoucherOptionLabel}
                value={templateVoucher || null}
                onChange={(_, val) => {
                  const sku = val?.sku || "";
                  setTemplateSku(sku);
                  setErrors((prev) => ({ ...prev, template_sku: undefined }));
                  loadTemplateDenominations(sku);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Template SKU *"
                    error={Boolean(errors.template_sku)}
                    helperText={
                      errors.template_sku ||
                      "Pick a SKU to load denomination rows — same % config applies to all selected SKUs"
                    }
                  />
                )}
              />

              <Autocomplete
                multiple
                options={vouchers}
                getOptionLabel={formatVoucherOptionLabel}
                value={vouchers.filter((v) => selectedSkus.includes(v.sku))}
                onChange={(_, vals) => setSelectedSkus(vals.map((v) => v.sku))}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    {formatVoucherOptionLabel(option)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target SKUs *"
                    error={Boolean(errors.skus)}
                    helperText={
                      errors.skus ||
                      `${selectedSkus.length} SKU(s) · SKUs with mismatched denominations will fail`
                    }
                  />
                )}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={form.discount_enabled}
                    onChange={(e) => updateField("discount_enabled", e.target.checked)}
                  />
                }
                label="Enable discount"
              />

              <FormControl disabled={!form.discount_enabled}>
                <FormLabel>Discount type</FormLabel>
                <RadioGroup
                  row
                  value={form.discount_type}
                  onChange={(e) => updateField("discount_type", e.target.value)}
                >
                  <FormControlLabel value="upto" control={<Radio />} label="Upto" />
                  <FormControlLabel value="fixed" control={<Radio />} label="Fixed" />
                </RadioGroup>
              </FormControl>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Per-denomination discount (%)
                </Typography>
                {errors.denominations && (
                  <FormHelperText error sx={{ mb: 1 }}>
                    {errors.denominations}
                  </FormHelperText>
                )}
                <DenominationTable
                  rows={form.denominations}
                  discountType={form.discount_type}
                  errors={errors}
                  loading={loadingDenoms}
                  disabled={!form.discount_enabled}
                  onChange={(denominations) => {
                    setForm((prev) => ({ ...prev, denominations }));
                    setErrors((prev) => ({ ...prev, denominations: undefined }));
                  }}
                />
              </Box>

              <TextField
                type="number"
                label="Max discount cap (₹)"
                value={form.max_discount_cap}
                onChange={(e) => updateField("max_discount_cap", e.target.value)}
                error={Boolean(errors.max_discount_cap)}
                helperText={errors.max_discount_cap || "Optional (0 = no cap)"}
                inputProps={{ min: 0, step: "0.01" }}
                disabled={!form.discount_enabled}
                fullWidth
              />

              <TextField
                type="number"
                label="Cashback override (%)"
                value={form.cashback_percent}
                onChange={(e) => updateField("cashback_percent", e.target.value)}
                error={Boolean(errors.cashback_percent)}
                helperText={
                  errors.cashback_percent ||
                  "Leave empty to inherit global cashback. Can be combined with per-denomination discount above."
                }
                inputProps={{ min: 0, step: "0.01" }}
                fullWidth
              />

              <Stack direction="row" spacing={2}>
                <Button type="submit" variant="contained" disabled={loading || loadingDenoms}>
                  {loading ? "Submitting..." : "Bulk Create"}
                </Button>
                <Button variant="outlined" onClick={() => navigate("/discounts")}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Card>

        {result && (
          <Card sx={{ p: 3, mt: 3, maxWidth: 760 }}>
            <Typography variant="h6" mb={2}>
              Results
            </Typography>
            <Alert severity="success" sx={{ mb: 2 }}>
              Created: {result.created?.length || 0} config(s)
            </Alert>
            {(result.failed?.length || 0) > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography color="error">
                    Failed: {result.failed.length} SKU(s)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {result.failed.map((f) => (
                      <Typography key={f.sku} variant="body2">
                        <strong>{f.sku}</strong>: {f.reason}
                      </Typography>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </Card>
        )}
      </Body>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
